// https://github.com/airbnb/react-dates/blob/master/src/shapes/DateRangePickerShape.js
// https://github.com/airbnb/react-dates/blob/master/examples/DateRangePickerWrapper.jsx

import * as React from 'react';
import moment from 'moment';
import classnames from 'classnames';
import DateRangePicker from 'react-dates/lib/components/DateRangePicker';

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { isInclusivelyAfterDay } from 'react-dates';

import '../date-picker-overrides.scss';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';
import { DATE_TIME_FORMAT, HTML_DATE_FORMAT } from 'utils/date';

import type {
  Data,
  DatePickerMultiDate,
  DatePickerMultiLabel,
  DatePickerMulti as DatePickerMultiType,
  DatePickerRenderDropdowns,
  MixedSelectDropdownItem
} from 'types';
import type { DatePickerMultiProps as Props } from './DatePickerMultiProps.type';

applyTranslations(require('./date-picker-multi.i18n.json'));

type Direction = 'left' | 'right';

type State = {
  focusedInput: DatePickerMultiLabel | undefined | null,
  key: string
};

class DatePickerMulti extends React.PureComponent<Props, State> {
  static defaultProps = {
    startDateName: 'startDate',
    endDateName: 'endDate',
    numMonths: 2,
    noPastDates: true,
    noFutureDates: false,
    showDateSelector: false,
    numPastYearsInDateSelector: 1,
    numFutureYearsInDateSelector: 0,
    maxAllowableDate: '',
    minAllowableDate: ''
  };

  state = {
    focusedInput: null,
    key: moment().format(DATE_TIME_FORMAT)
  };

  getDateDropdownsProp(): DatePickerMultiType | undefined | null {
    const { showDateSelector } = this.props;

    if (showDateSelector) {
      return {
        renderMonthElement: this.renderDateDropdowns
      };
    }

    return null;
  }

  generatePreviousYears(): Array<number> {
    const { numPastYearsInDateSelector, numFutureYearsInDateSelector, minAllowableDate, maxAllowableDate } = this.props;
    const curYear = moment().year();

    const numYearsBackForMinDate = minAllowableDate ? moment().diff(minAllowableDate, 'years') : 0;
    const numYearsForwardForMaxDate = maxAllowableDate ? moment().diff(maxAllowableDate, 'years') : 0;

    const numYearsBack = curYear - Math.max(numYearsBackForMinDate, (numPastYearsInDateSelector || 0));
    const numYearsForward = curYear + Math.max(numYearsForwardForMaxDate, (numFutureYearsInDateSelector || 0));
    const years: Array<number> = [];

    for (let i = numYearsForward; i >= numYearsBack; i--) {
      years.push(i);
    }

    return years;
  }

  isOutsideRange = (date: string): boolean => {
    const { noPastDates, noFutureDates, maxAllowableDate } = this.props;
    const today = moment();
    const DAY = 'day';

    return (noFutureDates ? today.endOf(DAY).isBefore(date) : false)
      || (noPastDates ? today.startOf(DAY).isAfter(date) : false)
      || (maxAllowableDate ? isInclusivelyAfterDay(date, moment(maxAllowableDate, HTML_DATE_FORMAT).add(1, DAY)) : false);
  };

  handleDatesChange = (
    {
      startDate,
      endDate
    }: DatePickerMultiDate
  ): void => {
    const { onChange } = this.props;
    const dates = {
      startDate: startDate && moment(startDate).format(HTML_DATE_FORMAT),
      endDate: endDate && moment(endDate).format(HTML_DATE_FORMAT)
    };

    if (onChange) {
      onChange(dates.startDate, dates.endDate);
    }
  };

  handleFocusChange = (focusedInput: DatePickerMultiLabel): void => {
    this.setState({
      focusedInput,
      key: moment().format(DATE_TIME_FORMAT)
    });
  };

  getInitialVisibleMonth = () => {
    const { startDate, endDate } = this.props;
    const { focusedInput } = this.state;

    if (focusedInput === 'endDate') {
      return moment(endDate || startDate || undefined);
    }

    return moment(startDate || undefined);
  };

  renderCalendarIcon(): React.ReactNode {
    return (
      <SVGDynamicIcon
        name="calendar-custom-fg"
        width={18}
        height={18}
        customColor="grey"
      />
    );
  }

  renderArrowIcon(direction: Direction): React.ReactNode {
    return (
      <SVGDynamicIcon
        name={`arrow-${direction}-custom-bg`}
        width={17}
        height={9}
        customColor="grey"
      />
    );
  }

  renderNavArrow(direction: Direction): React.ReactNode {
    const navContainerClasses = classnames('date-picker-multi__nav-container', {
      [`date-picker-multi__nav-container--direction-${direction}`]: Boolean(direction)
    });

    return (
      <div className={navContainerClasses}>
        <div className="date-picker-multi__nav">
          {this.renderArrowIcon(direction)}
        </div>
      </div>
    );
  }

  renderDateDropdowns = (
    {
      month,
      onMonthSelect,
      onYearSelect
    }: DatePickerRenderDropdowns
  ): React.ReactNode | undefined | null => {
    const { showDateSelector } = this.props;

    if (showDateSelector) {
      const previousYearsArray = this.generatePreviousYears();

      return (
        <div className="date-picker-multi__dropdowns">
          <MixedSelectDropdown
            key="date-picker-dropdowns__dropdown--month"
            name="date-picker-dropdowns__dropdown"
            items={moment.months().map((label, value) => ({
              identifier: String(value),
              isDisabled: false,
              isSelected: month.month() === value,
              label,
              value: String(value)
            }))}
            withSelectedInList
            withSmallSize
            withTruncate
            className="date-picker-dropdowns__dropdown date-picker-dropdowns__dropdown--month"
            onItemsToggle={(
              updatedItems: Array<MixedSelectDropdownItem>,
              data?: Data | null,
              selectedItem?: Partial<MixedSelectDropdownItem> | null
            ): void => (
              onMonthSelect(month, Number(selectedItem?.identifier || updatedItems[0].identifier)
            ))} // eslint-disable-line indent
          />
          <MixedSelectDropdown
            key="date-picker-dropdowns__dropdown--year"
            name="date-picker-dropdowns__dropdown"
            items={previousYearsArray.map(year => ({
              identifier: String(year),
              isDisabled: false,
              isSelected: month.year() === year,
              label: String(year),
              value: String(year)
            }))}
            withSelectedInList
            withSmallSize
            withTruncate
            className="date-picker-dropdowns__dropdown  date-picker-dropdowns__dropdown--year"
            onItemsToggle={(
              updatedItems: Array<MixedSelectDropdownItem>,
              data?: Data | null,
              selectedItem?: Partial<MixedSelectDropdownItem> | null
            ): void => (
              onYearSelect(month, Number(selectedItem?.identifier || updatedItems[0].identifier)
            ))} // eslint-disable-line indent
          />
        </div>
      );
    }

    return null;
  };

  render(): React.ReactNode {
    const {
      startDate,
      endDate,
      startDateName,
      endDateName,
      numMonths,
      isDisabled,
      calendarInfo,
      maxAllowableDate,
      minAllowableDate,
      isDayBlocked
    } = this.props;
    const { focusedInput } = this.state;
    const dates = {
      startDate: (startDate) ? moment(startDate) : undefined,
      endDate: (endDate) ? moment(endDate) : undefined
    };

    return (
      <div className="date-picker-multi">
        <DateRangePicker
          key={this.state.key}
          disabled={isDisabled}
          minimumNights={0} // allows to select the same start and end dates
          startDate={dates.startDate}
          startDateId={startDateName}
          startDatePlaceholderText={__t('date_picker_multi.placeholder_start_date')}
          endDate={dates.endDate}
          endDateId={endDateName}
          endDatePlaceholderText={__t('date_picker_multi.placeholder_end_date')}
          minDate={minAllowableDate ? moment(minAllowableDate) : undefined}
          maxDate={maxAllowableDate ? moment(maxAllowableDate) : undefined}
          focusedInput={focusedInput}
          numberOfMonths={window.innerWidth <= 680 ? 1 : numMonths}
          navPrev={this.renderNavArrow('left')}
          navNext={this.renderNavArrow('right')}
          customInputIcon={this.renderCalendarIcon()}
          customArrowIcon={this.renderArrowIcon('right')}
          inputIconPosition="before"
          displayFormat="DD/MM/YYYY"
          hideKeyboardShortcutsPanel
          renderCalendarInfo={() => calendarInfo}
          isOutsideRange={this.isOutsideRange}
          onDatesChange={this.handleDatesChange}
          onFocusChange={this.handleFocusChange}
          initialVisibleMonth={this.getInitialVisibleMonth}
          isDayBlocked={isDayBlocked || (() => false)}
          {...this.getDateDropdownsProp()}
        />
      </div>
    );
  }
}

export default DatePickerMulti;
